import type { DeliveryCreateInput, DeliveryFormValues } from "../types";

const TYPE_MAP: Record<string, string> = {
  standard: "STANDARD",
  "to-return": "TO_RETURN",
  "two-way-delivery": "TWO_WAY_DELIVERY",
  "quality-check": "QUALITY_CHECK",
  okm: "OKM",
};

// ─── Create Input ───────────────────────────────────────────────────────────

export function transformToCreateInput(
  values: DeliveryFormValues,
  type: string
): DeliveryCreateInput {
  const { step0, step1, step2, lastStep } = values;
  const isTwoWay = type === "two-way-delivery";

  const vehicles = [{ ...step1.vehicle, toRetrieve: false }];
  if (isTwoWay && step2.returnVehicle.carType) {
    vehicles.push({ ...step2.returnVehicle, toRetrieve: true });
  }

  return {
    type: TYPE_MAP[type] ?? "STANDARD",
    status: "PROFESSIONAL",
    numberOfVehicles: 1,

    // Client
    clientFirstName: step0.clientFirstName,
    clientLastName: step0.clientLastName,
    clientEmail: step0.clientEmail,
    clientPhone: step0.clientPhone.replace(/\s/g, ""),
    clientCompany: step0.clientCompany,
    clientSiret: step0.clientSiret,
    clientDrivingLicenceNumber: step0.clientDrivingLicenceNumber,

    // Mandataire / Representative
    representativeFirstName: step0.representativeFirstName,
    representativeLastName: step0.representativeLastName,
    representativeEmail: step0.representativeEmail,
    representativePhone: step0.representativePhone.replace(/\s/g, ""),
    representativeCompany: step0.representativeCompany,
    representativeSiret: step0.representativeSiret || "", // Fallback to empty string

    // IDs
    ...(step0.selectedMandataireId && {
      clientId: Number(step0.selectedMandataireId),
    }),

    // Dates
    carAvailabilityDate: step0.carAvailabilityDate,
    carPickUpDate: step0.carPickUpDate,
    carDeliveryDeadline: step0.carDeliveryDeadline,

    // Addresses
    fromAddress: step1.fromAddress,
    toAddress: step1.toAddress,
    ...(isTwoWay &&
      step2.toReturnFromAddress.address && {
        toReturnFromAddress: step2.toReturnFromAddress,
      }),
    ...(isTwoWay &&
      step2.toReturnToAddress.address && {
        toReturnToAddress: step2.toReturnToAddress,
      }),

    // Vehicles
    vehicles,

    // Options
    selectedOptions: lastStep.selectedOptions.selected.map((opt) => ({
      optionId: Number(opt.id),
      ...(opt.file ? { file: opt.file } : {}),
      ...(opt.input ? { input: opt.input } : {}),
    })),

    // Misc
    category: lastStep.category,
    comment: lastStep.comment,
    docs: lastStep.docs,
  };
}

// ─── Update Input ───────────────────────────────────────────────────────────

export function transformToUpdateInput(
  values: DeliveryFormValues,
  deliveryId: number,
  type: string
): DeliveryCreateInput & { id: number } {
  const base = transformToCreateInput(values, type);

  // Strip fields not allowed in update
  const { status, organizationId, clientId, ...updateFields } = base;

  return { id: deliveryId, ...updateFields };
}
